/**
 * assembly/input/tokenize.ts —— 输入管线纯内核（触发器解析；§7.1）
 * **逐字移植自 102 `frontend_src/src/assembly/input/tokenize.js`（含全部注释与判据）**，Vue/JS→React/TS。
 * 依据：施工方案 §八 5-A（编码级）／设计件 §7.1（输入管线 `/` 与 `@`）
 * A10（一套机制不造第二套）：`/` 命令与 `@` 引用走**同一个扫描器** scanTrigger ——
 *   两者只是 sigil 与产物 kind 不同，边界规则、命名规则、转义规则全共用。
 *
 * Token 形（kind ∈ {'/','@','text','ref'}）：
 *   { kind:'/',   sigil:'/', name, raw, start, end }   — `/name`（命令；name 可为空串＝裸 sigil）
 *   { kind:'@',   sigil:'@', name:'', raw, start, end } — 裸 `@`（引用触发器已敲、名字未完）
 *   { kind:'ref', sigil:'@', name, raw, start, end }    — `@name`（引用实体，name 非空）
 *   { kind:'text', raw, start, end }                    — 其余正文
 *
 * 规则（两 sigil 共用，§7.1）：
 *   - 触发位：行首或**空白之后**（词边界），避免邮箱 `a@b`、路径 `a/b` 误触发；
 *   - name 字符：`[A-Za-z0-9._-]`，引用额外允许 `/`（相对路径引用）；空白终止；
 *   - 无 name 的 `/` 也是合法 token（用户刚敲下 `/`，命令菜单应打开）；
 *   - 纯函数：不读时钟、不碰 DOM、不改入参。
 * 【U-1c 追加】`tokenAtCursor(text, cursor)`：光标处触发判定（菜单开合用），零 DOM（光标由调用方传入）。
 */

export type Token =
  | { kind: "/"; sigil: "/"; name: string; raw: string; start: number; end: number }
  | { kind: "@"; sigil: "@"; name: ""; raw: string; start: number; end: number }
  | { kind: "ref"; sigil: "@"; name: string; raw: string; start: number; end: number }
  | { kind: "text"; raw: string; start: number; end: number };

/** sigil 合法 name 字符（引用比命令多允许路径分隔符 `/`） */
function nameChar(ch: string, allowPath: boolean): boolean {
  if (/[A-Za-z0-9._-]/.test(ch)) return true;
  return allowPath && ch === "/";
}

/** 共用触发扫描器（A10 唯一解析机制）。从 index i 起，若 text[i] 是 sigil 且满足词边界，
 *  消费 sigil ＋ 连续 name 字符，返回 token；否则返回 null。 */
function scanTrigger(text: string, i: number, sigil: "/" | "@", allowPath: boolean): Token | null {
  if (text[i] !== sigil) return null;
  if (i > 0 && !/\s/.test(text[i - 1])) return null; // 词边界：前一字符必须是空白（或串首）
  let j = i + 1;
  while (j < text.length && nameChar(text[j], allowPath)) j += 1;
  const raw = text.slice(i, j);
  const name = raw.slice(1);
  if (sigil === "/") return { kind: "/", sigil, name, raw, start: i, end: j };
  // '@'：有名字 ⇒ ref；无名字 ⇒ 裸触发器 '@'
  return name
    ? { kind: "ref", sigil, name, raw, start: i, end: j }
    : { kind: "@", sigil, name: "", raw, start: i, end: j };
}

/** tokenize(text) → Token[]：单次左→右扫描；`/` 与 `@` 各自调用同一 scanTrigger（同一套规则）。
 *  非法输入（null/非串）⇒ 返回空数组（不抛错；调用方按无 token 处理）。 */
export function tokenize(text: unknown): Token[] {
  if (typeof text !== "string" || text.length === 0) return [];
  const tokens: Token[] = [];
  let buf = "";       // 累积中的正文
  let bufStart = 0;   // buf 在原文中的起点
  for (let i = 0; i < text.length;) {
    const t = scanTrigger(text, i, "/", false) || scanTrigger(text, i, "@", true);
    if (t) {
      if (buf) { tokens.push({ kind: "text", raw: buf, start: bufStart, end: i }); buf = ""; }
      tokens.push(t);
      i = t.end;
      bufStart = i;
    } else {
      if (!buf) bufStart = i;
      buf += text[i];
      i += 1;
    }
  }
  if (buf) tokens.push({ kind: "text", raw: buf, start: bufStart, end: text.length });
  return tokens;
}

/** 是否命令提交（§7.2 硬规矩：命令行绝不静默降级为普通提示词）。
 *  判据：首个 token 是 kind='/' 且 name 非空（裸 `/` 还在选菜单，不算提交命令）。 */
export function isCommandLead(tokens: readonly Token[]): boolean {
  const first = tokens[0];
  return !!first && first.kind === "/" && !!first.name;
}

/** 光标处触发（§7.1 触发行：「**光标处**键入 `/` 或 `@` ⇒ 打开分组候选菜单」）。
 *  判据＝光标落在某个 sigil token 的（sigil ＋ 名字）段内 —— 含**末端**（刚敲完名字也要出菜单），
 *  不含 sigil 之前（'a|/x' 不算）。`/` 与 `@` 同函数判定（A10 一套机制）。
 *  🔴 光标由调用方从 DOM 读入（`selectionStart`）——本模块**零 DOM**。 */
export function tokenAtCursor(text: unknown, cursor?: number): Token | null {
  if (typeof text !== "string" || text.length === 0) return null;
  const pos = Number.isInteger(cursor) ? (cursor as number) : text.length;
  for (const t of tokenize(text)) {
    if (t.kind === "text") continue;
    if (pos > t.start && pos <= t.end) return t;
  }
  return null;
}

/** 首个 ref token 的名字表（提交载荷用；无 ⇒ 空数组） */
export function refNames(tokens: readonly Token[]): string[] {
  return tokens.filter((t) => t.kind === "ref").map((t) => (t as { name: string }).name);
}
